import React from "react";
import moment from "moment-timezone";

import { Button, Checkbox, DatePicker, Form, Icon, Input, Modal, Table, Tooltip, Upload, message } from "antd";

import { _getSupplierPriceListLinesDownload, _getSupplierPriceListUploadFile, _postSupplierPriceListLinesUpload } from "../../services";
import { TIMEZONE, errorMessage, saveBlob, toDateTime } from "../term-contract/helpers";
import { showUploadResults } from "../term-contract/lines";
import { FULL_LIST_HELP, XLSX_DERIVED, XLSX_OPTIONAL, XLSX_REQUIRED, defaultValidTo, formatDate } from "./helpers";

const SOURCE_ACCEPT = ".pdf,.xlsx,.xls,.csv,.eml,.msg,.png,.jpg,.jpeg"; // eezee-server SOURCE_TYPES
const COUNT_NAMES = ["created", "updated", "unchanged", "restored", "dropped", "error"];

export const LINE_KEY_COLUMN = { title: "Line", dataIndex: "lineKey" };

/** File date, valid to (follows the file date + 12 months until edited), full list, ref: the fields every upload and import carries. */
export class UploadFields extends React.Component {

    setFileDate = ({
        fileDate,
    }) => {
        const { value, onChange } = this.props;
        onChange({
            ...value,
            fileDate,
            /* the picker's day as text, so a browser east of Singapore does not shift it */
            validTo: value.validToEdited ? value.validTo : (fileDate ? moment.tz(defaultValidTo({ date: fileDate.format("YYYY-MM-DD") }), TIMEZONE) : null)
        });
    };

    render() {
        const { value, onChange } = this.props;
        return (
            <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                    <Form.Item label="File date" required>
                        <DatePicker value={value.fileDate} format="YYYY-MM-DD" onChange={fileDate => this.setFileDate({ fileDate })} />
                    </Form.Item>
                    <Form.Item label="Valid to" required>
                        <DatePicker value={value.validTo} format="YYYY-MM-DD" onChange={validTo => onChange({ ...value, validTo, validToEdited: true })} />
                    </Form.Item>
                    <Form.Item label="Ref (e.g. quote no.)">
                        <Input value={value.sourceRef} onChange={e => onChange({ ...value, sourceRef: e.target.value })} />
                    </Form.Item>
                </div>
                <Checkbox checked={value.fullList} onChange={e => onChange({ ...value, fullList: e.target.checked })}>
                    Full list <span style={{ color: "rgba(0,0,0,0.45)" }}>· {FULL_LIST_HELP}</span>
                </Checkbox>
            </div>
        );
    }
}

/* a "YYYY-MM-DD" read by the model may be anything: an unreadable one is treated as missing */
const validDay = ({
    date,
}) => {
    const day = date ? moment.tz(date, "YYYY-MM-DD", true, TIMEZONE) : null;
    return day && day.isValid() ? day : null;
};

/** UploadFields' starting value: the given (suggested) dates, else file date today and valid to 12 months on. */
export const uploadFieldsValue = ({
    fileDate,
    validTo,
    sourceRef,
}) => {
    const day = validDay({ date: fileDate }) || moment.tz(TIMEZONE).startOf("day");
    const end = validDay({ date: validTo });
    return {
        fileDate: day,
        validTo: end || moment.tz(defaultValidTo({ date: day }), TIMEZONE),
        validToEdited: !!end,
        sourceRef: sourceRef || "",
        fullList: false
    };
};

/** The upload's fields as the API takes them; null when a required date is missing. */
export const uploadFieldsBody = ({
    value,
}) => value.fileDate && value.validTo ? {
    fileDate: value.fileDate.format("YYYY-MM-DD"),
    validTo: value.validTo.format("YYYY-MM-DD"),
    fullList: value.fullList,
    sourceRef: value.sourceRef.trim() || undefined
} : null;

/** Upload a filled template (.xlsx) with its file date, validity, ref and, optionally, the supplier's original file. */
class TemplateUploadModal extends React.Component {

    state = { file: null, sourceFile: null, fields: uploadFieldsValue({}), uploading: false };

    upload = async () => {
        const { priceList, onUploaded } = this.props;
        const { file, sourceFile, fields } = this.state;
        const body = uploadFieldsBody({ value: fields });
        if (!file || !body) {
            message.error("The template file, file date and valid to are required.");
            return;
        }
        this.setState({ uploading: true });
        try {
            const { data } = await _postSupplierPriceListLinesUpload({ id: priceList._id, file, sourceFile, ...body });
            showUploadResults({ results: data.results, warnings: data.warnings, keyColumn: LINE_KEY_COLUMN });
            onUploaded();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            this.setState({ uploading: false });
        }
    };

    render() {
        const { onCancel } = this.props;
        const { file, sourceFile, fields, uploading } = this.state;
        return (
            <Modal visible width={640} title="Upload template" okText="Upload" confirmLoading={uploading} onOk={this.upload} onCancel={onCancel}>
                <Form layout="vertical">
                    <Form.Item label="Template (.xlsx)" required>
                        <Upload accept=".xlsx" showUploadList={false} beforeUpload={f => { this.setState({ file: f }); return false; }}>
                            <Button><Icon type="upload" /> {file ? file.name : "Pick file"}</Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item label="The supplier's original file" help="Optional. Kept with this upload, for reference.">
                        <Upload accept={SOURCE_ACCEPT} showUploadList={false} beforeUpload={f => { this.setState({ sourceFile: f }); return false; }}>
                            <Button><Icon type="paper-clip" /> {sourceFile ? sourceFile.name : "Pick file"}</Button>
                        </Upload>
                    </Form.Item>
                    <UploadFields value={fields} onChange={value => this.setState({ fields: value })} />
                </Form>
            </Modal>
        );
    }
}

/** The secondary template actions: format help, download (every live line as the template), upload. */
export class TemplateActions extends React.Component {

    state = { uploading: false, downloading: false };

    download = async () => {
        const { priceList } = this.props;
        this.setState({ downloading: true });
        try {
            const { data } = await _getSupplierPriceListLinesDownload({ id: priceList._id });
            saveBlob({ blob: data, fileName: `price-list-${priceList.sellerName || priceList.sellerId}-${priceList.currency}.xlsx` });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        } finally {
            this.setState({ downloading: false });
        }
    };

    render() {
        const { priceList, readOnly, onUploaded } = this.props;
        const { uploading, downloading } = this.state;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Tooltip overlayStyle={{ maxWidth: 480 }} title={
                    <span>
                        Required: <code>{XLSX_REQUIRED.join(", ")}</code>. Optional (a blank cell clears it): <code>{XLSX_OPTIONAL.join(", ")}</code>.
                        Derived, ignored on upload: <code>{XLSX_DERIVED.join(", ")}</code>. Prices are before tax; only standing price lists, not quotations.
                        Download gives every line in this layout, so edit that file and upload it back.
                    </span>
                }>
                    <Icon type="question-circle" aria-label="Template format" style={{ color: "rgba(0,0,0,0.45)" }} />
                </Tooltip>
                <Button loading={downloading} onClick={this.download}><Icon type="download" /> Download template</Button>
                {!readOnly && <Button onClick={() => this.setState({ uploading: true })}><Icon type="upload" /> Upload template</Button>}
                {uploading && (
                    <TemplateUploadModal
                        priceList={priceList}
                        onCancel={() => this.setState({ uploading: false })}
                        onUploaded={() => {
                            this.setState({ uploading: false });
                            onUploaded();
                        }}
                    />
                )}
            </span>
        );
    }
}

/** The uploads tab: every file received, newest first, with its counts and the files to download. */
export class UploadsTable extends React.Component {

    download = async ({
        upload,
        which,
    }) => {
        try {
            const { data } = await _getSupplierPriceListUploadFile({ id: this.props.priceList._id, uploadId: upload._id, which });
            const file = which === "source" ? upload.sourceFile : upload.templateFile;
            saveBlob({ blob: data, fileName: file.name });
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    render() {
        const { uploads, loading, adminName } = this.props;
        return (
            <Table
                rowKey="_id"
                size="small"
                loading={loading}
                dataSource={uploads}
                pagination={false}
                columns={[
                    { title: "File date", dataIndex: "fileDate", render: date => formatDate({ date }) },
                    { title: "Valid to", dataIndex: "validTo", render: date => formatDate({ date }) },
                    { title: "Ref", dataIndex: "sourceRef", render: v => v || "—" },
                    { title: "Full list", dataIndex: "fullList", render: v => v ? "Yes" : "No" },
                    {
                        title: "Counts",
                        dataIndex: "counts",
                        render: counts => COUNT_NAMES.filter(name => counts && counts[name]).map(name => `${counts[name]} ${name}`).join(", ") || "—"
                    },
                    { title: "Uploaded", key: "uploaded", render: (_, upload) => `${toDateTime({ date: upload.createdAt })} by ${adminName({ userId: upload.createdBy })}` },
                    {
                        title: "Files",
                        key: "files",
                        render: (_, upload) => (
                            <span>
                                {upload.sourceFile && <a onClick={() => this.download({ upload, which: "source" })}>{upload.sourceFile.name}</a>}
                                {upload.sourceFile && upload.templateFile && " · "}
                                {upload.templateFile && <a onClick={() => this.download({ upload, which: "template" })}>template</a>}
                            </span>
                        )
                    }
                ]}
            />
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/uploads.js